import { NgClass } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { cloneDeep } from 'lodash-es'

import { DateOnly, resolveProperty } from '@nx-superprep/utils'
import { DrawerState } from '../drawer.component'
import { ResponsiveComponent } from '@nx-superprep/shared'

import { LessonTile } from '@nx-superprep/study'
import { _defaultDrawerStates } from '../../@shared'
import { LessonsListComponent } from './lessons-list/lessons-list.component'
import { NotificationComponent } from './notification/notification.component'
import { PerformanceChartsComponent } from './performance-charts/performance-charts.component'
import { PerformanceStatsComponent } from './performance-stats/performance-stats.component'
import { PerformanceCharts, PerformanceStats } from '@nx-superprep/backend/firebase'
import { LessonTermsComponent } from './lesson-terms/lesson-terms.component'

@Component({
  standalone: true,
  selector: 'pop-ui-drawer-content',
  templateUrl: './drawer-content.component.html',
  styleUrls: ['./drawer-content.component.scss'],
  imports: [ NgClass, PerformanceChartsComponent, PerformanceStatsComponent, LessonsListComponent, LessonTermsComponent, NotificationComponent ],
})
export class DrawerContentComponent extends ResponsiveComponent {
  notifications = {
    completed: {
      header: 'Way to go!',
      text: 'You completed all assignments.',
      icon: '/assets/icons/iconcheckmark-dark.svg',
      textColor: '#19805A',
      color: '#0000000a'
    },
    notEnrolled: {
      header: 'Hey there!',
      text: 'Not enrolled in a unit? Check out the library for resources to learn something new.',
      textColor: '#000000',
      color: '#0000000a',
      action: () => this.router.navigate(['/library'], {replaceUrl: true, skipLocationChange: true }),
      actionText: 'Add a unit'
    },
    noHistory: {
      header: 'Hmm...',
      text: 'Nothing studied on this day.',
      textColor: '#000000',
      color: '#0000000a'
    },
    recommended: {
      header: 'Recommended',
      text: 'Here are some recommendations we think you may be interested in.',
      textColor: '#000000',
      color: '#0000000a'
    },
    noDone: {
      header: 'Hmm...',
      text: 'No unit completed on this day.',
      textColor: '#000000',
      color: '#0000000a'
    },
    noPlan: {
      header: 'Hmm...',
      text: 'No plan for this day.',
      textColor: '#000000',
      color: '#0000000a'
    }
  }
  private _cloneCharts(charts: PerformanceCharts[]) {
    const _charts = cloneDeep(charts ? ['retention', 'correct', 'studied', ''].includes(this._selected??'') ? charts.filter((v:PerformanceCharts) => this._selected === v.name) : charts : charts)
    _charts.forEach((chart) => { if(chart.options?.scales?.['y']) { chart.options.scales['y'].display = this.isWeb }})
    return _charts
  }
  private _state: DrawerState = _defaultDrawerStates('empty')
  private _activeTab = (this._state.headerState.activeTab ?? 'all').toLowerCase()
  private _selected = this._state.contentState.selected?.toLowerCase()
  private _charts: PerformanceCharts[] = []
  private _stats: PerformanceStats[] = []

  @Input()
  get state() { return this._state }
  set state(value) {
    if (!value) { return }
    this._state = value
    this._activeTab = (this._state.headerState.activeTab ?? 'all').toLowerCase()
    this._selected = this._state.contentState.selected?.toLowerCase()
    this._stats = this._state.contentState.stats?.find(s => s.range === this._activeTab)?.stats ?? []
    if (value?.contentState.selected) {
      if (this.isPerformanceCharts) {
        this._charts = this._cloneCharts(this._state.contentState.charts?.find(s => s.range === this._activeTab)?.charts ?? [])
      }
      this.stateChange.emit(this._state)
    }
  }
  @Output() stateChange = new EventEmitter<DrawerState>()
  @Input() title = ''
  @Input() subtitle = ''

  constructor() {
    super()
    this.rootClassName = 'drawer-content-root-class-name'
  }

  get notification() {
    if (this.isLibraryLessons) { return; }
    if (this.isInPast) {
      if (this.isTodo) {
        if (this.isComplete) { return this.notifications.completed; }
        if (this.isEmpty('Active')) { return this.notifications.noHistory; }
      }
      if (this.isDone) {
        if (this.isEmpty('Active')) { return this.notifications.noHistory; }
        if (this.isEmpty('Done')) { return this.notifications.noDone; }
      }
      if (this.isAll) {
        if (this.isComplete) { return this.notifications.completed; }
        if (this.isEmpty('All')) { return this.isNoPlan ? this.notifications.noPlan : this.notifications.notEnrolled; }
      }
    }
    if (this.isInToday) {
      if (this.isPerformanceStats) {
        if (this.isEmpty('Stats') && this.isPerformanceLoaded) { return this.notifications.noHistory; } else { return undefined; }
      } else {
        if (this.isTodo) {
          if (this.isComplete) { return this.notifications.completed; }
          if (this.isEmpty('Active')) { return this.notifications.notEnrolled; }
        }
        if (this.isDone) {
          if (this.isEmpty('Done')) { return this.notifications.noDone; }
        }
        if (this.isAll) {
          if (this.isComplete) { return this.notifications.completed; }
          if (this.isEmpty('All')) { return this.isNoPlan ? this.notifications.noPlan : this.notifications.notEnrolled; }
        }
      }
    }
    if (this.isInFuture) {
      if (this.isTodo) {
        if (this.isEmpty('Active')) { return this.notifications.notEnrolled; }
      }
      if (this.isAll) {
        if (this.isEmpty('All')) { return this.isNoPlan ? this.notifications.noPlan : this.notifications.notEnrolled; }
      }
    }
    return this.lessonTiles.length === 0 ? this.notifications.notEnrolled : undefined
  }

  get contentState() {
    return this.state.contentState
  }

  get lesson() {
    return this.contentState?.lesson
  }

  get charts() {
    return this._charts
  }

  get stats() {
    return this._stats
  }

  get activeTab() {
    return this._activeTab
  }

  get date() {
    return this.contentState?.date ?? new DateOnly()
  }

  get isNoPlan() {
    return (this.state.headerState.tabs ?? [])[0]?.disabled
  }

  isEmpty(tabname: 'Active' | 'Done' | 'All' | 'Performance' | 'Stats' | 'Charts'): boolean {
    switch (tabname) {
      case 'Stats': return this.lessonTiles.length === 0
      case 'Charts': return this.charts.length === 0
      case 'Performance': return this.isEmpty('Stats') && this.isEmpty('Charts')
      case 'Active': return (this.lessonTiles.filter(t => !resolveProperty(t.finished) && (typeof t.progress === 'number' && t.progress < 1)).length === 0)
      case 'Done': return (this.lessonTiles.filter(t => resolveProperty(t.finished) || (typeof t.progress === 'number' && t.progress >= 1)).length === 0)
      case 'All': return this.lessonTiles.length === 0
      default: return this.isEmpty(this.isTodo ? 'Active' : 'Done')
    }
  }

  get isComplete() { return this.isEmpty('Active') && !this.isEmpty('Done'); }

  isActiveTab(name: string) {
    return this.activeTab.toLowerCase() === name.toLowerCase()
  }

  get isTodo() {
    return this.isActiveTab('Active')
  }

  get isDone() {
    return this.isActiveTab('Done')
  }

  get isAll() {
    return this.isActiveTab('All')
  }

  get isNone() {
    return this.isActiveTab('')
  }

  get isInToday() {
    return this.date.isToday()
  }

  get isInPast() {
    return this.date.isBefore(new DateOnly())
  }

  get isInFuture() {
    return this.date.isAfter(new DateOnly())
  }

  get lessonTiles() {
    return (this.contentState?.tiles ?? []) as LessonTile[]
  }

  get isDayPlanLessons() {
    return this.state.type === 'dayplan-lessons'
  }

  get isLibraryLessons() {
    return this.state.type === 'library-lessons' || this.state.type === 'onboarding-lessons'
  }

  get isPerformanceLessons() {
    return this.isPerformanceStats
  }

  get isPerformance() {
    return this.isPerformanceStats || this.isPerformanceCharts
  }

  get isPerformanceLoaded() {
    return this.isPerformance && !!this.contentState?.isLoaded
  }

  get isPerformanceStats() {
    return this.state.type === 'performance-stats'
  }

  get isPerformanceCharts() {
    return this.state.type === 'performance-charts'
  }

  get isLessonCompletion() {
    return this.state.type === 'lesson-completion'
  }

  get contentClassName() {
    return this.isPerformance ? 'performance' : this.isLibraryLessons ? 'library' : 'study'
  }

  get hasLessonList() {
    return this.isLibraryLessons || this.isDayPlanLessons || this.isPerformanceLessons
  }

  get hasNotification() {
    return !this.isPerformanceCharts
  }

  get hasRecommendation() {
    return false; // this.isInPast() && this.isTodo() && !this.isEmpty()
  }
}
